import { AavoDataGrid } from "src/components/common/dataGrid/AavoDataGrid.tsx";
import { floatColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faBoxOpen, faPrint, faUndo } from "@fortawesome/pro-regular-svg-icons";
import { downloadFile } from "src/utils/fileDownloading.ts";
import { RefreshableElementProps, RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import { useInputDialog } from "src/components/common/dialogs/input/useInputDialog.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { openFormOnDialog } from "src/components/common/dialogs/formDialog/openFormOnDialog.ts";
import { DeliverySourcePickingApi } from "src/api/generated/erp/delivery/api/deliverySourcePickingApi.ts";
import { WarehousePickingObjectLocationView } from "src/api/generated/erp/db/types/tables/warehousePickingObjectLocationView.ts";
import { DeliveryPackagePickingForm } from "src/components/views/erp/delivery/deliveryPackage/picking/DeliveryPackagePickingForm.tsx";
import { DeliveryPackageSource } from "src/api/generated/erp/delivery/model/deliveryPackageSource.ts";
import { DeliveryPackageWithSourcesView } from "src/api/generated/erp/db/types/tables/deliveryPackageWithSourcesView.ts";
import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { MenuCheckbox } from "src/components/common/contextMenu/MenuCheckbox.tsx";
import { AggregatedPickingLocation } from "src/api/generated/erp/delivery/service/aggregatedPickingLocation.ts";
import { AggregatedDeliveryPackagePickingForm } from "src/components/views/erp/delivery/deliveryPackage/picking/AggregatedDeliveryPackagePickingForm.tsx";
import { AavoSelectField } from "src/components/common/inputFields/AavoSelectField.tsx";
import { genericNullableValue } from "src/utils/genericNullableValue.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface DeliveryPackageSourcePickingDataGridProps extends RefreshableElementProps {
	deliveryPackageSource: DeliveryPackageSource;
	deliveryPackage: DeliveryPackageWithSourcesView | undefined;
	pickedLinesViewRefreshRef: RefreshableElementRef;
	reservableLinesViewRefreshRef: RefreshableElementRef;
	deliverySourceHasAutomaticReservation: boolean;
	revertReservation: (params: {
		pickingObjectLocation: WarehousePickingObjectLocationView;
		quantityToRevertInBaseUnit: number;
	}) => Promise<unknown>;
	purchaseOrderId?: number;
}

interface RowDto {
	rowId: string | number;
	rowType: "single" | "aggregated";
	configurationOrPartDescription: string | null | undefined;
	reservedQuantity: number;
	partUnit: string | null | undefined;
	locationCode: string | null | undefined;
	partId: number;
	partWarehouseLocationId: number | null | undefined;
	configurationContentHash: string | null | undefined;
	objectLineNumber?: number | null | undefined;
	objectMark?: string | null | undefined;
	subProjectDescription?: string | null | undefined;
	activityName?: string | null | undefined;
	warehousePickingObjectLocation?: WarehousePickingObjectLocationView;
	aggregatedPickingLocation?: AggregatedPickingLocation;
}

interface SubProjectOption {
	subProjectId: number;
	subProjectDescription: string;
}

interface ProjectActivityOption {
	activityId: number;
	activityName: string;
}

interface FetchDataResult {
	rows: RowDto[];
	activityOptions: ProjectActivityOption[];
	subProjectOptions: SubProjectOption[];
}

interface PickingDataGridParams {
	aggregateByPart: boolean;
	activityId: number | null;
	subProjectId: number | null;
}

export const DeliveryPackageSourcePickingDataGrid = ({
	deliveryPackageSource,
	deliveryPackage,
	refreshRef,
	pickedLinesViewRefreshRef,
	reservableLinesViewRefreshRef,
	deliverySourceHasAutomaticReservation,
	revertReservation,
	purchaseOrderId,
}: DeliveryPackageSourcePickingDataGridProps) => {
	const showInputDialog = useInputDialog();
	const { openDialog } = useGenericDialog();

	return (
		<ClientSideDataGridModel<FetchDataResult, RowDto, PickingDataGridParams>
			fetchData={async ({ aggregateByPart, activityId, subProjectId }) =>
				fetchAndMapData({
					deliveryPackageSource,
					aggregateByPart,
					subProjectId,
					activityId,
					purchaseOrderId,
				})
			}
			getRows={(data) => data.rows}
			gridId={"AC8A87916768FBE8"}
			getRowId={getPickingGridRowId}
			initialParams={{
				aggregateByPart: false,
				activityId: genericNullableValue<number>(),
				subProjectId: genericNullableValue<number>(),
			}}
			storedParams={["aggregateByPart"]}
			refreshRef={refreshRef}
			render={({ data, dataGridProps, refreshData, selectedRows, onlySelectedRow, currentParams }) => {
				const aggregateByPartEnabled = currentParams.aggregateByPart;

				return (
					<AavoDataGrid<RowDto>
						checkboxSelection
						columns={
							aggregateByPartEnabled ?
								[
									textColumn({
										field: "configurationOrPartDescription",
										headerName: i18n.t("part"),
										width: 400,
									}),
									floatColumn({
										field: "reservedQuantity",
										headerName: i18n.t("pickable_quantity"),
										width: 150,
									}),
									textColumn({
										field: "partUnit",
										headerName: i18n.t("unit"),
										width: 90,
									}),
									textColumn({
										field: "locationCode",
										headerName: i18n.t("warehouse_location"),
										width: 160,
									}),
								]
							:	[
									integerColumn({
										field: "objectLineNumber",
										headerName: i18n.t("line_number"),
										width: 90,
									}),
									textColumn({
										field: "configurationOrPartDescription",
										headerName: i18n.t("part"),
										width: 300,
									}),
									deliveryPackageSource.type === "project" && [
										textColumn({
											field: "subProjectDescription",
											headerName: i18n.t("sub_project"),
											width: 220,
										}),
										textColumn({
											field: "activityName",
											headerName: i18n.t("activity"),
											width: 220,
										}),
									],
									textColumn({
										field: "objectMark",
										headerName: i18n.t("mark"),
										width: 220,
									}),
									floatColumn({
										field: "reservedQuantity",
										headerName: i18n.t("pickable_quantity"),
										width: 150,
									}),
									textColumn({
										field: "partUnit",
										headerName: i18n.t("unit"),
										width: 90,
									}),
									textColumn({
										field: "locationCode",
										headerName: i18n.t("warehouse_location"),
										width: 160,
									}),
								]
						}
						actionBarComponents={
							<>
								{deliveryPackageSource.type === "project" && !aggregateByPartEnabled && (
									<>
										<AavoSelectField
											label={i18n.t("sub_project")}
											options={data.subProjectOptions}
											getOptionKey={(o: SubProjectOption) => o.subProjectId}
											getOptionLabel={(o) => o.subProjectDescription}
											value={currentParams.subProjectId}
											onChange={(subProjectId) => refreshData({ subProjectId })}
											placeholder={i18n.t("all")}
										/>
										<AavoSelectField
											label={i18n.t("activity")}
											options={data.activityOptions}
											getOptionKey={(o: ProjectActivityOption) => o.activityId}
											getOptionLabel={(o) => o.activityName}
											value={currentParams.activityId}
											onChange={(activityId) => refreshData({ activityId })}
											placeholder={i18n.t("all")}
										/>
									</>
								)}
								<AsyncButton
									label={i18n.t("picking_list")}
									variant={"outlined"}
									icon={faPrint}
									onClick={async () => {
										const selectedWarehousePickingObjectLocationIds =
											getSelectedWarehousePickingObjectLocationIds(selectedRows);

										const fileHandle =
											await DeliverySourcePickingApi.printDeliveryPackageSourcePickingList({
												source: deliveryPackageSource,
												warehousePickingObjectLocationIds:
													selectedWarehousePickingObjectLocationIds,
												aggregateByPart: aggregateByPartEnabled ? true : undefined,
											});
										downloadFile(fileHandle);
									}}
								/>
								<AsyncButton
									label={i18n.t("pick")}
									variant={"outlined"}
									icon={faBoxOpen}
									disabled={
										selectedRows.length === 0 ? i18n.t("select_line")
										: deliveryPackage === undefined ?
											i18n.t("select_package")
										: deliveryPackage.state !== "INITIAL" ?
											i18n.t("package_is_picked")
										:	false
									}
									onClick={openPickingView}
								/>
								{!deliverySourceHasAutomaticReservation && (
									<AsyncButton
										label={i18n.t("revert_reservation")}
										variant={"outlined"}
										icon={faUndo}
										disabled={
											aggregateByPartEnabled ? true
											: !onlySelectedRow ?
												i18n.t("select_a_single_row")
											: !isWarehousePickingObjectLocationView(onlySelectedRow) ?
												true
											: (
												onlySelectedRow.warehousePickingObjectLocation
													.objectAcquisitionMethod !== "WAREHOUSE"
											) ?
												i18n.t(
													"reservation_can_be_reverted_only_for_lines_with_warehouse_acquisition",
												)
											:	undefined
										}
										onClick={onRevertClick}
									/>
								)}
							</>
						}
						actionBarMenuComponents={[
							<MenuCheckbox
								key={"aggregateByPart"}
								label={i18n.t("aggregate_by_part")}
								checked={currentParams.aggregateByPart}
								onChange={async (value) => {
									await refreshData({ aggregateByPart: value });
								}}
							/>,
						]}
						agentContext={{
							key: defineAgentUiContextKey("erp.delivery.deliveryPackageSourcePicking.grid"),
							data: {
								deliveryPackageId: deliveryPackage?.deliveryPackageId ?? null,
								sourceType: deliveryPackageSource.type,
								customerOrderId:
									deliveryPackageSource.type === "customerOrder" ? deliveryPackageSource.customerOrderId : null,
								projectId: deliveryPackageSource.type === "project" ? deliveryPackageSource.projectId : null,
								aggregateByPart: aggregateByPartEnabled,
							},
							rowFields: ["rowId", "partId", "partWarehouseLocationId", "objectLineNumber"],
							selectedRowPathSegment: (row) => `${i18n.t("package_lines")} ${row.rowId}`,
						}}
						{...dataGridProps}
					/>
				);

				function openPickingView() {
					if (deliveryPackage?.deliveryPackageId == null) return;

					if (aggregateByPartEnabled) {
						const aggregatedRows = selectedRows
							.filter(isAggregatedPickingLocation)
							.map((row) => row.aggregatedPickingLocation);

						openFormOnDialog({
							openDialog: openDialog,
							title: i18n.t("pick"),
							size: "md",
							component: AggregatedDeliveryPackagePickingForm,
							agentContext: {
								key: defineAgentUiContextKey("erp.delivery.aggregatedDeliveryPackagePicking.dialog"),
								title: i18n.t("pick"),
								data: {
									deliveryPackageId: deliveryPackage.deliveryPackageId,
									partIds: aggregatedRows.map((row) => row.partId),
								},
							},
							props: {
								deliveryPackageId: deliveryPackage.deliveryPackageId,
								aggregatedPickingLocations: aggregatedRows,
							},
							onSubmit: async () => {
								await Promise.all([pickedLinesViewRefreshRef?.refresh(), refreshData()]);
							},
						});
					} else {
						const pickingObjectLocations = selectedRows
							.filter(isWarehousePickingObjectLocationView)
							.map((row) => row.warehousePickingObjectLocation);

						openFormOnDialog({
							openDialog: openDialog,
							title: i18n.t("pick"),
							size: "md",
							component: DeliveryPackagePickingForm,
							agentContext: {
								key: defineAgentUiContextKey("erp.delivery.deliveryPackagePicking.dialog"),
								title: i18n.t("pick"),
								data: {
									deliveryPackageId: deliveryPackage.deliveryPackageId,
									warehousePickingObjectLocationIds: pickingObjectLocations.map(
										(location) => location.warehousePickingObjectLocationId,
									),
								},
							},
							props: {
								deliveryPackageId: deliveryPackage.deliveryPackageId,
								pickingObjectLocations: pickingObjectLocations,
							},
							onSubmit: async () => {
								await Promise.all([pickedLinesViewRefreshRef?.refresh(), refreshData()]);
							},
						});
					}
				}

				async function onRevertClick() {
					if (!onlySelectedRow || !isWarehousePickingObjectLocationView(onlySelectedRow)) return;

					const revertQuantity = await showInputDialog({
						title: i18n.t("quantity_to_cancel"),
						type: "decimal",
						defaultValue: onlySelectedRow.warehousePickingObjectLocation.reservedQuantity,
						required: true,
					});
					if (revertQuantity == undefined) return;

					await revertReservation({
						pickingObjectLocation: onlySelectedRow.warehousePickingObjectLocation,
						quantityToRevertInBaseUnit: revertQuantity,
					});
					await Promise.all([reservableLinesViewRefreshRef?.refresh(), refreshData()]);
				}
			}}
		/>
	);
};

async function fetchAndMapData({
	deliveryPackageSource,
	aggregateByPart,
	activityId,
	subProjectId,
	purchaseOrderId,
}: {
	deliveryPackageSource: DeliveryPackageSource;
	aggregateByPart: boolean;
	activityId: number | null;
	subProjectId: number | null;
	purchaseOrderId?: number;
}) {
	if (aggregateByPart) {
		const locations = await DeliverySourcePickingApi.getAggregatedPickableLocationsForPackageSource({
			source: deliveryPackageSource,
			purchaseOrderId: purchaseOrderId ?? undefined,
		});
		const rows = locations.map(mapAggregatedPickingLocationToGridRow);
		return { rows, activityOptions: [], subProjectOptions: [] };
	}

	const locations = await DeliverySourcePickingApi.getPickableLocationsForPackageSource({
		source: deliveryPackageSource,
		purchaseOrderId: purchaseOrderId ?? undefined,
	});
	const allRows = locations.map(mapWarehousePickingObjectLocationToGridRow);

	const showProjectFilters =
		deliveryPackageSource.type === "project" && allRows.some((r) => r.warehousePickingObjectLocation != null);

	if (!showProjectFilters) {
		return { rows: allRows, activityOptions: [], subProjectOptions: [] };
	}

	const activityOptions = deriveActivityOptions(allRows);
	const subProjectOptions = deriveSubProjectOptions(allRows);

	const effectiveActivityId =
		activityId != null && activityOptions.some((o) => o.activityId === activityId) ? activityId : null;
	const effectiveSubProjectId =
		subProjectId != null && subProjectOptions.some((o) => o.subProjectId === subProjectId) ? subProjectId : null;

	let filteredRows = allRows;
	if (effectiveActivityId != null) {
		filteredRows = filteredRows.filter((r) => r.warehousePickingObjectLocation?.activityId === effectiveActivityId);
	}
	if (effectiveSubProjectId != null) {
		filteredRows = filteredRows.filter(
			(r) => r.warehousePickingObjectLocation?.subProjectId === effectiveSubProjectId,
		);
	}

	return { rows: filteredRows, activityOptions, subProjectOptions };
}

function isAggregatedPickingLocation(
	row: RowDto,
): row is RowDto & { aggregatedPickingLocation: AggregatedPickingLocation } {
	return row.rowType === "aggregated" && row.aggregatedPickingLocation != null;
}

function isWarehousePickingObjectLocationView(
	row: RowDto,
): row is RowDto & { warehousePickingObjectLocation: WarehousePickingObjectLocationView } {
	return row.rowType === "single" && row.warehousePickingObjectLocation != null;
}

function getPickingGridRowId(row: RowDto): string | number {
	return row.rowId;
}

function getSelectedWarehousePickingObjectLocationIds(selectedRows: RowDto[]): number[] | undefined {
	if (selectedRows.length === 0) return undefined;

	const rowIds = selectedRows.flatMap((row) => {
		if (isAggregatedPickingLocation(row)) {
			return row.aggregatedPickingLocation.sourceLocations.map(
				(sourceLocation) => sourceLocation.warehousePickingObjectLocationId,
			);
		}
		if (row.warehousePickingObjectLocation == null) return [];
		return [row.warehousePickingObjectLocation.warehousePickingObjectLocationId];
	});
	if (rowIds.length === 0) return undefined;
	return [...new Set(rowIds)];
}

function mapWarehousePickingObjectLocationToGridRow(location: WarehousePickingObjectLocationView): RowDto {
	return {
		...location,
		rowId: location.warehousePickingObjectLocationId,
		rowType: "single",
		warehousePickingObjectLocation: location,
	};
}

function mapAggregatedPickingLocationToGridRow(location: AggregatedPickingLocation): RowDto {
	return {
		...location,
		rowId: `${location.partId}_${location.partWarehouseLocationId}_${location.configurationContentHash ?? "null"}`,
		rowType: "aggregated",
		aggregatedPickingLocation: location,
	};
}

function deriveActivityOptions(rows: RowDto[]): ProjectActivityOption[] {
	const seen = new Set<number>();
	const result: ProjectActivityOption[] = [];
	for (const row of rows) {
		const loc = row.warehousePickingObjectLocation;
		if (loc?.activityId != null && !seen.has(loc.activityId)) {
			seen.add(loc.activityId);
			result.push({ activityId: loc.activityId, activityName: loc.activityName ?? "" });
		}
	}
	return result;
}

function deriveSubProjectOptions(rows: RowDto[]): SubProjectOption[] {
	const seen = new Set<number>();
	const result: SubProjectOption[] = [];
	for (const row of rows) {
		const loc = row.warehousePickingObjectLocation;
		if (loc?.subProjectId != null && !seen.has(loc.subProjectId)) {
			seen.add(loc.subProjectId);
			result.push({ subProjectId: loc.subProjectId, subProjectDescription: loc.subProjectDescription ?? "" });
		}
	}
	return result;
}
